/**
 * @file 送付先フォームに関するストア
 */

import { defineStore } from 'pinia'

const LOCAL_STORAGE_KEY = 'userForm'

export const useUserStore = defineStore({
  id: 'user',

  state: () => ({
    /**
     * 送付先フォームのデータ
     *
     * **⚠ LocalStorageと同期するために、`watch`などを使用して変更を監視し、変更直後に`store()`を呼び出すようにしてください。**
     */
    form: new RequestAppliForm(),

    /** 送付先フォームのエラーデータ */
    error: {
      /**
       * 全体のエラー文言
       *
       * e.g. `'内容をご確認の上、修正後、お申込みをお願いいたします。'`
       */
      message: null as string | null,
      /**
       * 個々のエラー文言
       *
       * e.g.
       * `
       * {
       *   name: '「姓」「名」を入力してください。',
       *   birthday: '「生年月」を入力してください。'
       * }
       * `
       */
      data: {} as { [key: string /** エラー項目の名前 e.g. name */]: string },
    },
  }),

  getters: {},

  actions: {
    /**
     * 初期化する
     *
     * 最初に実行するようにしてください。
     *
     * 1. LocalStorageからデータを取得
     * 2. 取得したデータの整合性を確認
     * 3. データをストアへ保持
     */
    init() {
      try {
        // LocalStorageからデータを取得。空ならエラーでcatch節へ
        const data = localStorage.getItem(LOCAL_STORAGE_KEY)
        if (!data) throw null

        // データをパース。パース失敗ならエラーでcatch節へ
        const parsed = JSON.parse(data)

        // ストアへ保持
        this.form.init(parsed)
      } catch {
        // 何らかのエラーの場合は、LocalStorageに初期化済みのデータを格納する
        this.store()
      }
    },

    /** 現在のフォームのデータをLocalStorageへ保存する */
    store() {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.form))
    },

    /** フォームを空にする */
    clear() {
      this.form.clear()
      localStorage.removeItem(LOCAL_STORAGE_KEY)
    },

    /** エラーをリセットする */
    clearError() {
      this.error.message = null
      this.error.data = {}
    },
  },
})

interface RequestAppliFormInitData {
  name1?: string | null
  name2?: string | null
  kana1?: string | null
  kana2?: string | null
  sex?: number | null
  birthdayYear?: string | null
  birthdayMonth?: string | null
  zip?: string | null
  prefecture?: number | null
  address1?: string | null
  address2?: string | null
  addressOther?: string | null
  tel?: string | null
  highSchPrefecture?: number | null
  highSchNpp?: number | null
  highSchName?: string | null
  grade?: number | null
  email?: string | null
  hopeCourse?: number | null
  hopeDepart1?: number | null
  hopeDepart2?: number | null
  hopeArea?: number | null
}

export class RequestAppliForm {
  name1: string
  name2: string
  kana1: string
  kana2: string
  sex: number | null
  birthdayYear: string
  birthdayMonth: string
  zip: string
  prefecture: number | null
  address1: string
  address2: string
  addressOther: string
  tel: string
  highSchPrefecture: number | null
  highSchNpp: number | null
  highSchName: string
  grade: number | null
  email: string
  hopeCourse: number | null
  hopeDepart1: number | null
  hopeDepart2: number | null
  hopeArea: number | null

  constructor(initData?: RequestAppliFormInitData) {
    this.name1 = initData?.name1 ?? ''
    this.name2 = initData?.name2 ?? ''
    this.kana1 = initData?.kana1 ?? ''
    this.kana2 = initData?.kana2 ?? ''
    this.sex = initData?.sex ?? null
    this.birthdayYear = initData?.birthdayYear ?? ''
    this.birthdayMonth = initData?.birthdayMonth ?? ''
    this.zip = initData?.zip ?? ''
    this.prefecture = initData?.prefecture ?? null
    this.address1 = initData?.address1 ?? ''
    this.address2 = initData?.address2 ?? ''
    this.addressOther = initData?.addressOther ?? ''
    this.tel = initData?.tel ?? ''
    this.highSchPrefecture = initData?.highSchPrefecture ?? null
    this.highSchNpp = initData?.highSchNpp ?? null
    this.highSchName = initData?.highSchName ?? ''
    this.grade = initData?.grade ?? null
    this.email = initData?.email ?? ''
    this.hopeCourse = initData?.hopeCourse ?? null
    this.hopeDepart1 = initData?.hopeDepart1 ?? null
    this.hopeDepart2 = initData?.hopeDepart2 ?? null
    this.hopeArea = initData?.hopeArea ?? null
  }

  // TODO: コンストラクタとinitの役割がほぼ同じになってしまっている
  init(initData?: RequestAppliFormInitData) {
    initData?.name1 && (this.name1 = initData.name1)
    initData?.name2 && (this.name2 = initData.name2)
    initData?.kana1 && (this.kana1 = initData.kana1)
    initData?.kana2 && (this.kana2 = initData.kana2)
    initData?.sex && (this.sex = initData.sex)
    initData?.birthdayYear && (this.birthdayYear = initData.birthdayYear)
    initData?.birthdayMonth && (this.birthdayMonth = initData.birthdayMonth)
    initData?.zip && (this.zip = initData.zip)
    initData?.prefecture && (this.prefecture = initData.prefecture)
    initData?.address1 && (this.address1 = initData.address1)
    initData?.address2 && (this.address2 = initData.address2)
    initData?.addressOther && (this.addressOther = initData.addressOther)
    initData?.tel && (this.tel = initData.tel)
    initData?.highSchPrefecture && (this.highSchPrefecture = initData.highSchPrefecture)
    initData?.highSchNpp && (this.highSchNpp = initData.highSchNpp)
    initData?.highSchName && (this.highSchName = initData.highSchName)
    initData?.grade && (this.grade = initData.grade)
    initData?.email && (this.email = initData.email)
    initData?.hopeCourse && (this.hopeCourse = initData.hopeCourse)
    initData?.hopeDepart1 && (this.hopeDepart1 = initData.hopeDepart1)
    initData?.hopeDepart2 && (this.hopeDepart2 = initData.hopeDepart2)
    initData?.hopeArea && (this.hopeArea = initData.hopeArea)
  }

  clear() {
    this.name1 = ''
    this.name2 = ''
    this.kana1 = ''
    this.kana2 = ''
    this.sex = null
    this.birthdayYear = ''
    this.birthdayMonth = ''
    this.zip = ''
    this.prefecture = null
    this.address1 = ''
    this.address2 = ''
    this.addressOther = ''
    this.tel = ''
    this.highSchPrefecture = null
    this.highSchNpp = null
    this.highSchName = ''
    this.grade = null
    this.email = ''
    this.hopeCourse = null
    this.hopeDepart1 = null
    this.hopeDepart2 = null
    this.hopeArea = null
  }

  dummy() {
    this.name1 = 'てすとせい'
    this.name2 = 'てすとめい'
    this.kana1 = 'テストセイ'
    this.kana2 = 'テストメイ'
    this.sex = 1
    this.birthdayYear = '1970'
    this.birthdayMonth = '12'
    this.zip = '123-4567'
    this.prefecture = 13
    this.address1 = 'てすと'
    this.address2 = 'てすと'
    this.addressOther = 'てすと'
    this.tel = '012-345-6789'
    this.highSchPrefecture = 13
    this.highSchNpp = 1
    this.highSchName = 'てすと'
    this.grade = 1
    this.email = 'test@test.test'
    this.hopeCourse = 1
    this.hopeDepart1 = 101
    this.hopeDepart2 = 102
    this.hopeArea = 4
  }
}
